import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../api";
import { getUser } from "../../auth";

export default function AdminHomePage() {
  const navigate = useNavigate();
  const user = getUser();
  const [counts, setCounts] = useState({ nominations: null, delegates: null, nomRegs: null, delRegs: null });

  useEffect(() => {
    Promise.allSettled([
      api.get("/jury/events"),
      api.get("/jury/delegate-events"),
      api.get("/jury/stats"),
    ]).then(([nomRes, delRes, statsRes]) => {
      const stats = statsRes.status === "fulfilled" ? statsRes.value.data.data : {};
      setCounts({
        nominations: nomRes.status === "fulfilled" ? (nomRes.value.data.data || []).length : 0,
        delegates:   delRes.status === "fulfilled" ? (delRes.value.data.data || []).length : 0,
        nomRegs:     stats.nominationCount ?? null,
        delRegs:     stats.delegateCount   ?? null,
      });
    });
  }, []);

  const displayName = user?.name || user?.firstName || "Admin";

  const cards = [
    {
      key: "delegates",
      title: "Delegates & Interests",
      icon: "🎟",
      desc: "Delegates · Speakers · Sponsors — all registrations across all events.",
      count: counts.delegates,
      regs: counts.delRegs,
      regsLabel: "registrations",
      gradient: "linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)",
      shadow: "rgba(99,102,241,0.25)",
      accent: "#6366F1",
      path: "/admin/delegates",
    },
    {
      key: "nominations",
      title: "Nominations",
      icon: "🏆",
      desc: "Manage award nominations, jury scoring, and winner selection.",
      count: counts.nominations,
      regs: counts.nomRegs,
      regsLabel: "nominations",
      gradient: "linear-gradient(135deg, #0EA5E9 0%, #1D9AEC 100%)",
      shadow: "rgba(29,154,236,0.25)",
      accent: "#1D9AEC",
      path: "/admin/events",
    },
  ];

  return (
    <Layout>
      {/* Welcome header */}
      <div style={{ marginBottom: 36 }}>
        <p style={{ margin: "0 0 4px", fontSize: 12, color: "#94A3B8", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Admin Dashboard
        </p>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: "#0F172A", letterSpacing: "-0.03em" }}>
          Welcome back, {displayName}
        </h1>
        <p style={{ margin: "6px 0 0", fontSize: 14, color: "#64748B" }}>
          Select a section to get started.
        </p>
      </div>

      {/* Two main cards */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, maxWidth: 820 }}>
        {cards.map(card => (
          <div
            key={card.key}
            onClick={() => navigate(card.path)}
            style={{
              borderRadius: 16, overflow: "hidden",
              boxShadow: `0 4px 20px ${card.shadow}`,
              cursor: "pointer",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = `0 12px 36px ${card.shadow}`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = "";
              e.currentTarget.style.boxShadow = `0 4px 20px ${card.shadow}`;
            }}
          >
            {/* Colored top */}
            <div style={{ background: card.gradient, padding: "32px 28px 28px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: 40, marginBottom: 14 }}>{card.icon}</div>
                  <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#fff", letterSpacing: "-0.03em" }}>
                    {card.title}
                  </h2>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
                  <div style={{ background: "rgba(255,255,255,0.18)", borderRadius: 10, padding: "8px 14px", textAlign: "center", backdropFilter: "blur(8px)", minWidth: 64 }}>
                    {card.count === null ? (
                      <div style={{ width: 24, height: 24, borderRadius: "50%", border: "3px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", animation: "spin 0.8s linear infinite", margin: "0 auto" }} />
                    ) : (
                      <>
                        <div style={{ fontSize: 26, fontWeight: 800, color: "#fff", lineHeight: 1 }}>{card.count}</div>
                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.75)", marginTop: 1 }}>events</div>
                      </>
                    )}
                  </div>
                  {card.regs !== null && (
                    <div style={{ background: "rgba(255,255,255,0.12)", borderRadius: 10, padding: "6px 14px", textAlign: "center", minWidth: 64 }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", lineHeight: 1 }}>{card.regs}</div>
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", marginTop: 1 }}>{card.regsLabel}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* White bottom */}
            <div style={{ background: "#fff", padding: "18px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <p style={{ margin: 0, fontSize: 13, color: "#64748B", lineHeight: 1.6, flex: 1 }}>
                {card.desc}
              </p>
              <span style={{ fontSize: 20, color: card.accent, marginLeft: 16, fontWeight: 700 }}>→</span>
            </div>
          </div>
        ))}
      </div>

      {/* Jury Members quick link */}
      <div style={{ marginTop: 20, maxWidth: 820 }}>
        <div
          onClick={() => navigate("/admin/jury")}
          style={{
            background: "#fff", borderRadius: 12, padding: "16px 24px",
            border: "1px solid #E2E8F0", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 14,
            transition: "box-shadow 0.15s, border-color 0.15s",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.08)"; e.currentTarget.style.borderColor = "#94A3B8"; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)"; e.currentTarget.style.borderColor = "#E2E8F0"; }}
        >
          <div style={{ fontSize: 26 }}>👥</div>
          <div>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#0F172A" }}>Jury Members</p>
            <p style={{ margin: 0, fontSize: 12, color: "#94A3B8" }}>Manage jury members and event assignments</p>
          </div>
          <span style={{ marginLeft: "auto", color: "#94A3B8", fontSize: 18 }}>→</span>
        </div>
      </div>
    </Layout>
  );
}
